import { getLandingPageConfig } from "../../../data/landingPageConfigs";
import {
  isMultiStudyConfig,
  LandingPageConfig,
  PreScreeningQuestion,
  TrialIdConfig,
} from "../../../types/landingPage";
import { mapQuestionnaireToAttributes } from "../../../utils/attributeMapping";
import {
  AttributeDefinitionType,
  AttributeValueInput,
} from "../../../utils/attributeMapping/types";
import {
  mapCanadianEthnicityToAttributes,
  mapUsEthnicityToAttributes,
} from "../../../utils/attributeMapping/ethnicityMapping";
import { formatPhoneNumber } from "../../../utils/countryUtils";
import { calculateAge, formatDateToYYYYMMDD } from "../../../utils/dateUtils";
import { track } from "../../../utils/rudderstack";
import { getCountryByCode } from "../../../utils/validationUtils";
import {
  ExistingPatientContactFormSchema,
  NewPatientContactFormSchema,
} from "../contactFormSchema";
import { EMR_CONSENT_TEMPLATE_VERSION } from "../EmrAcceptance";
import {
  QuestionnaireAnswers,
  questionnaireAnswersSchema,
} from "../utils/questionnaireStorage";
import {
  ExistingPatientSubmissionPayload,
  existingPatientSubmissionPayloadSchema,
  NewPatientSubmissionPayload,
  newPatientSubmissionPayloadSchema,
} from "./types";

export type NewPatientInput = {
  formData: NewPatientContactFormSchema;
  questionnaireAnswers: QuestionnaireAnswers;
  landingPageId: string;
};

export type ExistingPatientInput = {
  formData: ExistingPatientContactFormSchema;
  questionnaireAnswers: QuestionnaireAnswers;
  landingPageId: string;
  userCredentials?: ExistingPatientSubmissionPayload["userCredentials"];
};

export type PatientInput = NewPatientInput | ExistingPatientInput;

// Function that gets the trial ID of the matching trial or the default trial ID
// NOTE: This function does NOT handle conditionalRouting configs - those are handled by useConditionalTrialSelection hook
export function getMatchingTrialId(
  trialIdConfig: string | TrialIdConfig,
  questionnaireAnswers: QuestionnaireAnswers,
  dateOfBirth: string,
  landingPageId?: string,
): string | undefined {
  const prescreenFailed = questionnaireAnswers.prescreeningStatus === "failed";

  if (prescreenFailed) {
    track("lp.trial_id.assigned", {
      trial_id_type: "prescreen_failed",
      has_age_cohorts: false,
      is_default_trial_id: false,
      landing_page_id: landingPageId,
    });
    return undefined;
  }

  if (typeof trialIdConfig === "string") {
    track("lp.trial_id.assigned", {
      trial_id_type: "fixed",
      has_age_cohorts: false,
      is_default_trial_id: true,
      landing_page_id: landingPageId,
    });
    return trialIdConfig;
  }

  // Handle conditionalRouting configs (should not happen - these are handled by useConditionalTrialSelection)
  if ("conditionalRouting" in trialIdConfig) {
    console.warn(
      "getMatchingTrialId called with conditionalRouting config - this should be handled by useConditionalTrialSelection hook",
    );
    return undefined;
  }

  const participantAge = calculateAge(new Date(dateOfBirth));

  const matchingCohort = trialIdConfig.ageCohorts?.find(
    (ageCohort: {
      minAgeInclusive: number;
      maxAgeExclusive: number;
      trialId: string;
    }) =>
      participantAge >= ageCohort.minAgeInclusive &&
      participantAge < ageCohort.maxAgeExclusive,
  );

  if (matchingCohort) {
    track("lp.trial_id.assigned", {
      trial_id_type: "age_cohort",
      has_age_cohorts: true,
      is_default_trial_id: false,
      age_cohort_min: matchingCohort.minAgeInclusive,
      age_cohort_max: matchingCohort.maxAgeExclusive,
      age_within_cohort: true,
      age_value: participantAge,
      landing_page_id: landingPageId,
    });
    return matchingCohort.trialId;
  }

  // If no matching cohort found, check for default trial ID
  if (
    !matchingCohort &&
    "defaultTrialId" in trialIdConfig &&
    trialIdConfig.defaultTrialId
  ) {
    track("lp.trial_id.assigned", {
      trial_id_type: "default",
      has_age_cohorts: !!trialIdConfig.ageCohorts,
      is_default_trial_id: true,
      age_within_cohort: false,
      age_value: participantAge,
      landing_page_id: landingPageId,
    });
  }

  // This can return undefined if there is no default trial ID (cases where the patient doesn't match any age cohorts)
  return "defaultTrialId" in trialIdConfig
    ? trialIdConfig.defaultTrialId
    : undefined;
}

function getDerivedData({
  questionnaireAnswers,
  landingPageId,
  phoneNumber,
  ethnicity,
  country,
}: {
  questionnaireAnswers: QuestionnaireAnswers;
  dateOfBirth: string;
  landingPageId: string;
  phoneNumber: string;
  ethnicity?: string[];
  country: string;
}): {
  attributes: AttributeValueInput[];
  phoneE164: string;
  landingPageConfig: LandingPageConfig;
  localeCode: string;
} {
  const validatedAnswers =
    questionnaireAnswersSchema.parse(questionnaireAnswers);

  const landingPageConfig = getLandingPageConfig(landingPageId);

  if (!landingPageConfig) {
    throw new Error("Landing page config not found");
  }

  const countryObject = getCountryByCode(country);
  const phoneE164 = formatPhoneNumber(countryObject, phoneNumber);

  const localeCode = "EN";

  // Get attributes from prescreener questions
  // For multi-study pages, combine common questions + matched study's questions
  let screenerQuestions: PreScreeningQuestion[] = [];

  if (isMultiStudyConfig(landingPageConfig)) {
    // Multi-study page: combine common questions + matched study questions
    screenerQuestions = [...landingPageConfig.commonPreScreeningQuestions];

    // If a study was matched, include its specific questions for attribute mapping
    const matchedStudyId = validatedAnswers.matchedStudyId;
    if (matchedStudyId) {
      const matchedStudy = landingPageConfig.studies.find(
        (s) => s.studyId === matchedStudyId,
      );
      if (matchedStudy?.additionalQuestions) {
        screenerQuestions = [
          ...screenerQuestions,
          ...matchedStudy.additionalQuestions,
        ];
      }
    }
  } else if (landingPageConfig.preScreeningQuestions) {
    // Single-study page: use preScreeningQuestions directly
    screenerQuestions = landingPageConfig.preScreeningQuestions;
  }

  const attributes = mapQuestionnaireToAttributes(
    validatedAnswers.questionAnswers || {},
    screenerQuestions,
    landingPageId,
    country,
  );

  // Handle ethnicity mapping separately if ethnicity is provided
  if (ethnicity && ethnicity.length > 0) {
    const ethnicityAttributes =
      country === "CA"
        ? mapCanadianEthnicityToAttributes(ethnicity)
        : mapUsEthnicityToAttributes(ethnicity);

    // Convert ethnicity attributes to the expected AttributeValueInput format
    const convertedEthnicityAttributes: AttributeValueInput[] =
      ethnicityAttributes.map((attr) => {
        const base: AttributeValueInput = {
          attributeDefinitionId: attr.id,
        };

        switch (attr.type) {
          case AttributeDefinitionType.BOOLEAN:
            return { ...base, booleanAttributeValue: attr.value as boolean };
          case AttributeDefinitionType.RADIO:
            return { ...base, radioAttributeValue: attr.value as string };
          case AttributeDefinitionType.FLOAT:
            return { ...base, floatAttributeValue: attr.value as number };
          default:
            return base;
        }
      });

    // Combine prescreener attributes with ethnicity attributes
    attributes.push(...convertedEthnicityAttributes);
  }

  return {
    attributes,
    phoneE164,
    landingPageConfig,
    localeCode,
  };
}

export function prepareExistingPatientSubmissionPayload(
  {
    formData,
    questionnaireAnswers,
    landingPageId,
    userCredentials,
  }: ExistingPatientInput,
  ketchConsent?: string | null,
): ExistingPatientSubmissionPayload {
  const { attributes, phoneE164, landingPageConfig, localeCode } =
    getDerivedData({
      questionnaireAnswers,
      dateOfBirth: formData.dateOfBirth,
      landingPageId,
      phoneNumber: formData.phone,
      ethnicity: formData.ethnicity,
      country: formData.country,
    });

  const emrConsent =
    landingPageConfig.requireEmrConsent && formData.emrConsentAccepted
      ? {
          consentSignedAt: new Date().toISOString(),
          consentTemplateVersion: EMR_CONSENT_TEMPLATE_VERSION,
        }
      : undefined;

  return existingPatientSubmissionPayloadSchema.parse({
    userCredentials,
    condition: landingPageConfig.conditionId,
    firstName: formData.firstName,
    lastName: formData.lastName,
    sex: formData.sex,
    dob: formatDateToYYYYMMDD(formData.dateOfBirth),
    country: formData.country,
    suburb: formData.zipCode,
    address: formData.address,
    preferredLoginMethod: landingPageConfig.preferredLoginMethod,
    phoneNumber: phoneE164,
    localeCode,
    ketchConsent: ketchConsent ?? undefined,
    attributeValueInput: attributes,
    consentToHmTCsVersion: "latest",
    thirdPartyContactConsentVersion: formData.thirdPartyContactConsentVersion,
    trialSiteId: formData.trialSiteId,
    consentLocaleCode: formData.consentLocaleCode,
    emrConsent,
  });
}

// Function to prepare submission payload
export function prepareNewPatientSubmissionPayload(
  { formData, questionnaireAnswers, landingPageId }: NewPatientInput,
  ketchConsent?: string | null,
): NewPatientSubmissionPayload {
  const { attributes, phoneE164, landingPageConfig, localeCode } =
    getDerivedData({
      questionnaireAnswers,
      dateOfBirth: formData.dateOfBirth,
      landingPageId,
      phoneNumber: formData.phone,
      ethnicity: formData.ethnicity,
      country: formData.country,
    });

  const emrConsent =
    landingPageConfig.requireEmrConsent && formData.emrConsentAccepted
      ? {
          consentSignedAt: new Date().toISOString(),
          consentTemplateVersion: EMR_CONSENT_TEMPLATE_VERSION,
        }
      : undefined;

  return newPatientSubmissionPayloadSchema.parse({
    condition: landingPageConfig.conditionId,
    email: formData.email,
    firstName: formData.firstName,
    lastName: formData.lastName,
    sex: formData.sex,
    dob: formatDateToYYYYMMDD(formData.dateOfBirth),
    country: formData.country,
    suburb: formData.zipCode,
    address: formData.address,
    preferredLoginMethod: landingPageConfig.preferredLoginMethod,
    phoneNumber: phoneE164,
    localeCode,
    ketchConsent: ketchConsent ?? undefined,
    attributeValueInput: attributes,
    consentToHmTCsVersion: "latest",
    thirdPartyContactConsentVersion: formData.thirdPartyContactConsentVersion,
    trialSiteId: formData.trialSiteId,
    consentLocaleCode: formData.consentLocaleCode,
    emrConsent,
  });
}
